import { firebaseConfig as firebaseConfigJson } from '@ps/config';
import {
  type FirebaseApp,
  type FirebaseOptions,
  initializeApp,
} from 'firebase/app';

/**
 * Firebase **app** initialization only — imports `firebase/app` and nothing
 * else. Each service's instance getter lives in its own module so that a
 * consumer needing only one service doesn't transitively bundle the others:
 *
 *   - Auth      → `./auth`      (`getAuthInstance` / `auth`)
 *   - Firestore → `./firestore` (`getFirestoreInstance` / `firestore`)
 *   - Storage   → `./storage`   (`getStorageInstance` / `storage`)
 *
 * This matters for the public bio: its deferred auth imports only `./auth`, so
 * `firebase/firestore` (~390KB w/ offline persistence) and `firebase/storage`
 * never reach the visitor bundle. (Previously this module imported all four
 * SDKs, so touching auth dragged in firestore + storage.) Import the specific
 * service module, not the `index.ts` barrel, when you need just one service.
 */

export interface FirebaseConfig extends FirebaseOptions {
  authDomain?: string;
  databaseURL?: string;
}

/**
 * Get Firebase configuration with dynamic authDomain handling
 * Supports special handling for iframe-controller paths
 */
export function getFirebaseConfig(): FirebaseConfig {
  const config: FirebaseConfig = { ...firebaseConfigJson };

  // Handle dynamic authDomain for iframe-controller
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/iframe-controller/link-account')
  ) {
    config.authDomain = window.location.host;
  }

  return config;
}

let app: FirebaseApp | null = null;

/**
 * Initialize Firebase app (singleton pattern)
 */
export function initializeFirebase(): FirebaseApp {
  if (app) {
    return app;
  }

  const config = getFirebaseConfig();
  app = initializeApp(config);
  return app;
}

/**
 * Get Firebase app instance
 */
export function getApp(): FirebaseApp {
  if (!app) {
    return initializeFirebase();
  }
  return app;
}

// Initialize Firebase on module load (for browser environments)
if (typeof window !== 'undefined') {
  initializeFirebase();
}
